import { Copy, LogIn, LogOut, RefreshCw, RotateCcw, ShieldCheck, UserSearch } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type {
  AuthStatusResponse,
  DiagnosticsResponse,
  ExternalContactView,
  HealthResponse,
  MemberProfile,
  PublicWeComError,
  RuntimeConfigResponse,
  StepStatus
} from "../shared/types";
import { api, ApiClientError } from "./api";
import type { JssdkContextStatus } from "./context-status";
import { getJssdkContextMessage } from "./context-status";
import { extractRejectedIp, getPublicErrorMessage, isWeComIpRejected, mapOAuthErrorMessage } from "./error-mapping";
import { buildExternalContactDiagnosticForCopy, formatCustomerType } from "./external-contact-view";
import { canCallGetCurExternalContact } from "./get-cur-external-contact-flow";
import { OAUTH_STARTED_STORAGE_KEY, isOAuthStartThrottled, readOAuthError, shouldStartOAuth } from "./oauth-flow";
import {
  getCurrentExternalContactDiagnostic,
  getWeComJssdkMetadata,
  isWeComBrowser,
  registerWeComJssdk,
  type GetCurExternalContactDiagnostic
} from "./wecom-jssdk";
import { contextStatusFromGetExternalContactClassification } from "./wecom-jsapi-error";

type UiStepStatus = StepStatus | "not_in_wecom";
type StepKey =
  | "oauth"
  | "accessToken"
  | "configTicket"
  | "agentConfigTicket"
  | "jssdkRegister"
  | "getCurExternalContact"
  | "externalContactGet";

interface Diagnosis {
  step: string;
  message: string;
  wecomErrCode: number | null;
  wecomErrMsg: string | null;
  browserError: string | null;
  wrongEntry: boolean;
  trustedDomain: boolean;
  permission: boolean;
  signatureMismatch: boolean;
  blockedIp: string | null;
  actions: string[];
  getCurExternalContact: GetCurExternalContactDiagnostic | null;
}

const stepLabels: Record<StepKey, string> = {
  oauth: "OAuth",
  accessToken: "access_token",
  configTicket: "config ticket",
  agentConfigTicket: "agent_config ticket",
  jssdkRegister: "WECOM-JSSDK register",
  getCurExternalContact: "getCurExternalContact",
  externalContactGet: "externalcontact/get"
};

const initialSteps: Record<StepKey, UiStepStatus> = {
  oauth: "idle",
  accessToken: "idle",
  configTicket: "idle",
  agentConfigTicket: "idle",
  jssdkRegister: "idle",
  getCurExternalContact: "idle",
  externalContactGet: "idle"
};

export function App() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [runtimeConfig, setRuntimeConfig] = useState<RuntimeConfigResponse | null>(null);
  const [auth, setAuth] = useState<AuthStatusResponse | null>(null);
  const [diagnostics, setDiagnostics] = useState<DiagnosticsResponse | null>(null);
  const [member, setMember] = useState<MemberProfile | null>(null);
  const [externalContact, setExternalContact] = useState<ExternalContactView | null>(null);
  const [steps, setSteps] = useState<Record<StepKey, UiStepStatus>>(initialSteps);
  const [contextStatus, setContextStatus] = useState<JssdkContextStatus>("jssdk_not_started");
  const [jssdkReady, setJssdkReady] = useState(false);
  const [registerCompletedAt, setRegisterCompletedAt] = useState<string | null>(null);
  const [getCurExternalContactCalledAt, setGetCurExternalContactCalledAt] = useState<string | null>(null);
  const [lastVisibilityChangeAt, setLastVisibilityChangeAt] = useState<string | null>(null);
  const [diagnosis, setDiagnosis] = useState<Diagnosis>(() => createEmptyDiagnosis());
  const [copied, setCopied] = useState(false);
  const [isRefreshingExternalContact, setIsRefreshingExternalContact] = useState(false);
  const [now, setNow] = useState(new Date());
  const weComBrowser = useMemo(() => isWeComBrowser(), []);
  const mockMode = Boolean(runtimeConfig?.mockMode || auth?.mockMode);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    void bootstrap();
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setLastVisibilityChangeAt(new Date().toISOString());
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  async function bootstrap() {
    const oauthQueryError = readOAuthError(window.location.search);
    if (oauthQueryError) {
      sessionStorage.removeItem(OAUTH_STARTED_STORAGE_KEY);
      setSteps((current) => ({ ...current, oauth: "failure" }));
      setDiagnosis(buildOAuthQueryDiagnosis(oauthQueryError.oauthError, oauthQueryError.wecomErrcode));
    }

    await Promise.all([loadHealth(), loadRuntimeConfig()]);

    let status: AuthStatusResponse;
    try {
      status = await loadAuthStatus();
    } catch (error) {
      sessionStorage.removeItem(OAUTH_STARTED_STORAGE_KEY);
      setSteps((current) => ({ ...current, oauth: "failure" }));
      setDiagnosis(buildDiagnosis("OAuth 初始化", error));
      return;
    }

    if (status.authenticated) {
      sessionStorage.removeItem(OAUTH_STARTED_STORAGE_KEY);
      setSteps((current) => ({ ...current, oauth: "success" }));
      await refreshMember();
      return;
    }

    if (oauthQueryError) {
      return;
    }

    if (!weComBrowser) {
      setContextStatus("not_in_wecom");
      setSteps((current) => ({ ...current, oauth: "not_in_wecom" }));
      setDiagnosis(createBrowserOnlyDiagnosis());
      return;
    }

    const nowMs = Date.now();
    const storedTimestamp = sessionStorage.getItem(OAUTH_STARTED_STORAGE_KEY);
    if (isOAuthStartThrottled(nowMs, storedTimestamp)) {
      setSteps((current) => ({ ...current, oauth: "processing" }));
      setDiagnosis(createOAuthWaitingDiagnosis());
      return;
    }

    if (shouldStartOAuth(nowMs, storedTimestamp)) {
      startOAuth();
    }
  }

  async function loadHealth() {
    try {
      setHealth(await api.health());
    } catch (error) {
      setDiagnosis(buildDiagnosis("运行环境", error));
    }
  }

  async function loadRuntimeConfig() {
    try {
      setRuntimeConfig(await api.runtimeConfig());
    } catch (error) {
      setDiagnosis(buildDiagnosis("运行配置", error));
    }
  }

  async function loadDiagnostics() {
    try {
      const result = await api.diagnostics();
      setDiagnostics(result);
      setSteps((current) => ({
        ...current,
        accessToken: result.checks.accessToken.ok ? "success" : "failure",
        configTicket: result.checks.configTicket.ok ? "success" : "failure",
        agentConfigTicket: result.checks.agentConfigTicket.ok ? "success" : "failure"
      }));

      const blocked = [result.checks.accessToken, result.checks.configTicket, result.checks.agentConfigTicket]
        .map((check) => check.error)
        .find((error) => isWeComIpRejected(error?.wecomErrCode, error?.wecomErrMsg));
      if (blocked) {
        setContextStatus("jssdk_blocked");
        setDiagnosis(buildDiagnosis("服务诊断", new ApiClientError(502, { error: { code: "WECOM_API_ERROR", message: blocked.message, detail: blocked } })));
      }
    } catch (error) {
      setDiagnosis(buildDiagnosis("服务诊断", error));
    }
  }

  async function loadAuthStatus(): Promise<AuthStatusResponse> {
    const result = await api.authStatus();
    setAuth(result);
    setSteps((current) => ({
      ...current,
      oauth: result.oauthStatus
    }));
    return result;
  }

  async function refreshMember() {
    setSteps((current) => ({ ...current, oauth: "processing" }));
    try {
      const result = await api.me();
      setMember(result);
      setSteps((current) => ({ ...current, oauth: "success" }));
    } catch (error) {
      setMember(null);
      setSteps((current) => ({ ...current, oauth: "failure" }));
      setDiagnosis(buildDiagnosis("当前企业成员", error));
    }
  }

  async function initJssdk() {
    if (!weComBrowser) {
      setContextStatus("not_in_wecom");
      setDiagnosis(createBrowserOnlyDiagnosis());
      return;
    }
    if (!auth?.authenticated) {
      setDiagnosis(createOAuthRequiredDiagnosis());
      return;
    }

    setJssdkReady(false);
    setContextStatus("jssdk_initializing");
    setSteps((current) => ({
      ...current,
      accessToken: "processing",
      configTicket: "processing",
      agentConfigTicket: "processing",
      jssdkRegister: "processing"
    }));
    try {
      if (mockMode) {
        setJssdkReady(true);
        setRegisterCompletedAt(new Date().toISOString());
        setContextStatus("no_customer_context");
        setSteps((current) => ({
          ...current,
          accessToken: "success",
          configTicket: "success",
          agentConfigTicket: "success",
          jssdkRegister: "success"
        }));
        return;
      }

      await registerWeComJssdk((url) => api.signature(url), (error) => {
        setDiagnosis(buildDiagnosis("WECOM-JSSDK register", error));
      });
      setJssdkReady(true);
      setRegisterCompletedAt(new Date().toISOString());
      setContextStatus("no_customer_context");
      setSteps((current) => ({
        ...current,
        accessToken: "success",
        configTicket: "success",
        agentConfigTicket: "success",
        jssdkRegister: "success"
      }));
    } catch (error) {
      setContextStatus("jssdk_blocked");
      setSteps((current) => ({
        ...current,
        accessToken: "failure",
        configTicket: "failure",
        agentConfigTicket: "failure",
        jssdkRegister: "failure"
      }));
      setDiagnosis(buildDiagnosis("WECOM-JSSDK register", error));
    }
  }

  async function identifyExternalContact() {
    if (!canCallCurrentExternalContact()) {
      setDiagnosis(createCannotIdentifyCustomerDiagnosis(contextStatus));
      return;
    }

    setSteps((current) => ({ ...current, getCurExternalContact: "processing" }));
    try {
      const diagnostic = mockMode
        ? createMockGetCurExternalContactDiagnostic()
        : await getCurrentExternalContactDiagnostic();
      setGetCurExternalContactCalledAt(diagnostic.calledAt);

      if (!diagnostic.success || !diagnostic.userId) {
        const nextStatus = contextStatusFromGetExternalContactClassification(diagnostic.classification);
        setContextStatus(nextStatus);
        setSteps((current) => ({
          ...current,
          getCurExternalContact: "failure",
          externalContactGet: "idle"
        }));
        setDiagnosis(buildGetCurExternalContactDiagnosis(diagnostic));
        return;
      }

      const externalUserId = diagnostic.userId;
      setContextStatus("customer_identified");
      setSteps((current) => ({ ...current, getCurExternalContact: "success", externalContactGet: "processing" }));
      const result = await api.externalContact(externalUserId);
      setExternalContact(result);
      setSteps((current) => ({ ...current, externalContactGet: "success" }));
    } catch (error) {
      setContextStatus("unknown_get_external_contact_error");
      setSteps((current) => ({
        ...current,
        getCurExternalContact: "failure",
        externalContactGet: "idle"
      }));
      setDiagnosis(buildDiagnosis("getCurExternalContact", error));
    }
  }

  async function refreshExternalContactProfile() {
    if (isRefreshingExternalContact) {
      return;
    }

    const externalUserId = externalContact?.externalUserId;
    if (!externalUserId) {
      setDiagnosis({
        ...createEmptyDiagnosis(),
        step: "刷新客户资料",
        message: "当前还没有可刷新的 external_userid，请先重新识别当前客户。",
        actions: ["点击“重新识别当前客户”取得 external_userid 后，再刷新客户资料。"]
      });
      return;
    }

    setIsRefreshingExternalContact(true);
    setSteps((current) => ({ ...current, externalContactGet: "processing" }));
    try {
      const result = await api.externalContact(externalUserId);
      setExternalContact(result);
      setSteps((current) => ({ ...current, externalContactGet: "success" }));
    } catch (error) {
      setSteps((current) => ({ ...current, externalContactGet: "failure" }));
      setDiagnosis(buildDiagnosis("刷新客户资料", error));
    } finally {
      setIsRefreshingExternalContact(false);
    }
  }

  function canCallCurrentExternalContact(): boolean {
    return canCallGetCurExternalContact({
      isWeComBrowser: weComBrowser,
      authenticated: Boolean(auth?.authenticated),
      jssdkReady,
      mockMode
    });
  }

  function startOAuth() {
    sessionStorage.setItem(OAUTH_STARTED_STORAGE_KEY, Date.now().toString());
    window.location.replace("/api/auth/wecom/start");
  }

  async function resetOAuth() {
    try {
      await api.logout();
    } catch (error) {
      setDiagnosis(buildDiagnosis("清除测试会话", error));
    } finally {
      sessionStorage.removeItem(OAUTH_STARTED_STORAGE_KEY);
      setMember(null);
      setAuth(null);
      setSteps((current) => ({ ...current, oauth: "processing" }));
      window.location.replace("/api/auth/wecom/start");
    }
  }

  function restartOAuth() {
    sessionStorage.removeItem(OAUTH_STARTED_STORAGE_KEY);
    startOAuth();
  }

  async function copyDiagnostics() {
    const payload = sanitizeForCopy({
      health,
      runtimeConfig,
      auth,
      diagnostics,
      steps,
      contextStatus,
      member,
      externalContact: buildExternalContactDiagnosticForCopy(externalContact),
      diagnosis,
      getCurExternalContact: diagnosis.getCurExternalContact,
      timing: {
        registerCompletedAt,
        getCurExternalContactCalledAt,
        lastVisibilityChangeAt
      },
      client: {
        url: sanitizeUrlForDiagnostics(window.location.href),
        referrer: sanitizeUrlForDiagnostics(document.referrer),
        userAgent: window.navigator.userAgent,
        visibilityState: document.visibilityState,
        isHttps: window.location.protocol === "https:",
        isWeComBrowser: weComBrowser,
        ...getWeComJssdkMetadata()
      }
    });
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  const contextMessage = getJssdkContextMessage(contextStatus, diagnosis.blockedIp);
  const currentFollowRelation = externalContact?.currentFollowRelation ?? null;

  return (
    <main className="app-shell">
      <header className="app-header">
        <div>
          <p className="eyebrow">企业微信自建应用</p>
          <h1>企微侧边栏通路验证</h1>
        </div>
        {mockMode ? <span className="mock-badge">模拟数据</span> : null}
      </header>

      <section className="panel">
        <SectionTitle title="运行环境" />
        <KeyValue label="企微内置浏览器" value={weComBrowser ? "是" : "否"} tone={weComBrowser ? "success" : "muted"} />
        <KeyValue label="当前页面 URL" value={window.location.href} />
        <KeyValue label="HTTPS" value={window.location.protocol === "https:" ? "是" : "否"} />
        <KeyValue label="当前时间" value={now.toLocaleString()} />
        <KeyValue label="应用 AgentId" value={runtimeConfig?.agentId ?? "未获取"} />
        <KeyValue
          label="客户聊天上下文"
          value={contextMessage}
          tone={contextStatus === "customer_identified" ? "success" : contextStatus === "jssdk_blocked" ? "warning" : "muted"}
        />
        <KeyValue label="后端健康" value={health ? `${health.status} / ${health.nodeEnv}` : "未获取"} />
      </section>

      <section className="panel">
        <SectionTitle title="当前企业成员" />
        <ProfileRow avatar={member?.avatar} title={member?.name ?? "未识别"} subtitle={member?.userId ?? "等待 OAuth"} />
        <KeyValue label="识别状态" value={member?.identificationStatus ?? steps.oauth} tone={member ? "success" : "warning"} />
        <KeyValue label="UserId" value={member?.userId ?? "未获取"} />
        <KeyValue label="部门" value={member?.department?.join(", ") ?? "未返回"} />
        <KeyValue label="OAuth" value={steps.oauth} />
      </section>

      <section className="panel">
        <SectionTitle title="当前外部联系人" />
        <ProfileRow
          avatar={externalContact?.avatar}
          title={externalContact?.displayName ?? "未识别"}
          subtitle={externalContact ? `微信昵称：${externalContact.wechatName ?? "未返回"}` : "等待聊天工具栏上下文"}
        />
        {externalContact && !currentFollowRelation ? (
          <p className="relation-empty">未获取到当前销售与该客户的跟进关系。</p>
        ) : null}
        <div className="subsection">
          <h3>备注信息</h3>
          <KeyValue label="备注名" value={currentFollowRelation?.remark ?? "未设置"} />
          <KeyValue label="备注企业" value={currentFollowRelation?.remarkCorpName ?? "未设置"} />
          <MobileList mobiles={currentFollowRelation?.remarkMobiles ?? []} />
          <DescriptionValue label="描述" value={currentFollowRelation?.description ?? "未设置"} />
        </div>
        <div className="subsection">
          <h3>客户信息</h3>
          <KeyValue label="客户类型" value={externalContact ? formatCustomerType(externalContact.type) : "未返回"} />
          <KeyValue label="微信昵称" value={externalContact?.wechatName ?? "未返回"} />
          <KeyValue label="客户所属企业" value={externalContact?.customerCorpName ?? "未返回"} />
          <KeyValue label="跟进成员" value={formatFollowUserIds(externalContact?.followUserIds)} />
        </div>
        <details className="tech-details">
          <summary>技术信息</summary>
          <KeyValue label="external_userid" value={externalContact?.externalUserId ?? "未获取"} />
          <KeyValue label="上下文状态" value={contextStatus} />
          <KeyValue label="识别状态" value={externalContact?.identificationStatus ?? steps.getCurExternalContact} />
        </details>
      </section>

      <section className="panel">
        <SectionTitle title="鉴权状态" />
        <div className="step-list">
          {(Object.keys(stepLabels) as StepKey[]).map((key) => (
            <div className="step-row" key={key}>
              <span>{stepLabels[key]}</span>
              <StatusPill status={steps[key]} />
            </div>
          ))}
        </div>
      </section>

      <section className="panel">
        <SectionTitle title="错误诊断" />
        <KeyValue label="当前步骤" value={diagnosis.step} />
        <KeyValue label="诊断信息" value={diagnosis.message} />
        <KeyValue label="企微 errcode" value={diagnosis.wecomErrCode ?? "无"} />
        <KeyValue label="企微 errmsg" value={diagnosis.wecomErrMsg ?? "无"} />
        <KeyValue label="浏览器错误" value={diagnosis.browserError ?? "无"} />
        <KeyValue label="错误入口" value={diagnosis.wrongEntry ? "可能" : "未判断为主要原因"} />
        <KeyValue label="可信域名" value={diagnosis.trustedDomain ? "可能有问题" : "未判断为主要原因"} />
        <KeyValue label="应用权限" value={diagnosis.permission ? "可能有问题" : "未判断为主要原因"} />
        <KeyValue label="URL 签名" value={diagnosis.signatureMismatch ? "可能不一致" : "未判断为主要原因"} />
        <ul className="actions">
          {diagnosis.actions.map((action) => (
            <li key={action}>{action}</li>
          ))}
        </ul>
      </section>

      <section className="button-grid" aria-label="操作按钮">
        <button type="button" onClick={() => void identifyExternalContact()}>
          <UserSearch size={16} />
          重新识别当前客户
        </button>
        <button
          type="button"
          onClick={() => void refreshExternalContactProfile()}
          disabled={!externalContact?.externalUserId || isRefreshingExternalContact}
        >
          <RefreshCw size={16} />
          {isRefreshingExternalContact ? "刷新中" : "刷新客户资料"}
        </button>
        <button type="button" onClick={() => void initJssdk()}>
          <ShieldCheck size={16} />
          重新初始化 JS-SDK
        </button>
        <button type="button" onClick={() => void refreshMember()}>
          <RefreshCw size={16} />
          刷新成员信息
        </button>
        <button type="button" onClick={() => void loadDiagnostics()}>
          <RotateCcw size={16} />
          重新检查诊断
        </button>
        <button type="button" onClick={() => void copyDiagnostics()}>
          <Copy size={16} />
          {copied ? "已复制" : "复制诊断结果"}
        </button>
        <button type="button" onClick={restartOAuth}>
          <LogIn size={16} />
          重新发起 OAuth
        </button>
        <button type="button" className="danger" onClick={() => void resetOAuth()}>
          <LogOut size={16} />
          清除会话并 OAuth
        </button>
        <button type="button" onClick={() => void bootstrap()}>
          <RotateCcw size={16} />
          重新检查
        </button>
      </section>
    </main>
  );
}

function SectionTitle({ title }: { title: string }) {
  return <h2>{title}</h2>;
}

function ProfileRow({ avatar, title, subtitle }: { avatar?: string | null; title: string; subtitle: string }) {
  return (
    <div className="profile-row">
      <div className="avatar">
        <img
          src={avatar || "/default-avatar.svg"}
          alt=""
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = "/default-avatar.svg";
          }}
        />
      </div>
      <div>
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </div>
    </div>
  );
}

function MobileList({ mobiles }: { mobiles: string[] }) {
  return (
    <div className="kv">
      <span>备注电话</span>
      <div className="kv-content">
        {mobiles.length > 0 ? (
          <div className="mobile-list">
            {mobiles.map((mobile) => (
              <div className="mobile-item" key={mobile}>
                <span>{mobile}</span>
                <button type="button" onClick={() => void navigator.clipboard?.writeText(mobile).catch(() => undefined)}>
                  复制
                </button>
              </div>
            ))}
          </div>
        ) : (
          "未设置"
        )}
      </div>
    </div>
  );
}

function DescriptionValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="kv">
      <span>{label}</span>
      <strong className="customer-description">{value}</strong>
    </div>
  );
}

function KeyValue({
  label,
  value,
  tone = "default"
}: {
  label: string;
  value: string | number | boolean | null;
  tone?: "default" | "success" | "warning" | "muted";
}) {
  return (
    <div className={`kv ${tone}`}>
      <span>{label}</span>
      <strong>{String(value)}</strong>
    </div>
  );
}

function StatusPill({ status }: { status: UiStepStatus }) {
  const text: Record<UiStepStatus, string> = {
    idle: "未开始",
    processing: "处理中",
    success: "成功",
    failure: "失败",
    not_in_wecom: "not_in_wecom"
  };
  return <b className={`status ${status}`}>{text[status]}</b>;
}

function createEmptyDiagnosis(): Diagnosis {
  return {
    step: "暂无错误",
    message: "等待验证流程开始。",
    wecomErrCode: null,
    wecomErrMsg: null,
    browserError: null,
    wrongEntry: false,
    trustedDomain: false,
    permission: false,
    signatureMismatch: false,
    blockedIp: null,
    actions: ["在企业微信客户单聊工具栏中打开页面后，再执行客户识别。"],
    getCurExternalContact: null
  };
}

function createBrowserOnlyDiagnosis(): Diagnosis {
  return {
    ...createEmptyDiagnosis(),
    step: "运行环境",
    message: "普通浏览器不能取得企业成员身份。",
    actions: ["请在企业微信内置浏览器或自建应用入口中打开页面。"]
  };
}

function createOAuthWaitingDiagnosis(): Diagnosis {
  return {
    ...createEmptyDiagnosis(),
    step: "OAuth",
    message: "最近 60 秒内已发起 OAuth，正在等待企业微信回调。",
    actions: ["如果回调失败或页面没有变化，60 秒后可以重新发起 OAuth。"]
  };
}

function createOAuthRequiredDiagnosis(): Diagnosis {
  return {
    ...createEmptyDiagnosis(),
    step: "WECOM-JSSDK register",
    message: "需要先完成成员 OAuth，再初始化 JS-SDK。",
    actions: ["点击“重新发起 OAuth”，完成成员身份识别后再初始化 JS-SDK。"]
  };
}

function createCannotIdentifyCustomerDiagnosis(status: JssdkContextStatus): Diagnosis {
  return {
    ...createEmptyDiagnosis(),
    step: "getCurExternalContact",
    message: getJssdkContextMessage(status),
    actions: ["确认当前在企业微信浏览器、OAuth 已成功、JS-SDK 已初始化。"]
  };
}

function buildOAuthQueryDiagnosis(oauthError: string, wecomErrcode: number | null): Diagnosis {
  const message = mapOAuthErrorMessage(oauthError, wecomErrcode);
  return {
    ...createEmptyDiagnosis(),
    step: "OAuth",
    message,
    wecomErrCode: wecomErrcode,
    actions:
      oauthError === "wecom_api" && wecomErrcode === 60020
        ? ["将服务器出口 IP 配置到企业微信后台的企业可信 IP 或可信来源 IP。", "配置完成后重新发起 OAuth。"]
        : ["点击“重新发起 OAuth”重新授权。"]
  };
}

function buildDiagnosis(step: string, error: unknown): Diagnosis {
  const publicError = error instanceof ApiClientError ? error.payload.error.detail : null;
  const fallbackMessage = error instanceof Error ? error.message : String(error);
  const message = getPublicErrorMessage(publicError, fallbackMessage);
  const lowerMessage = message.toLowerCase();
  const code = publicError?.wecomErrCode ?? null;
  const blockedIp = extractRejectedIp(message);
  const ipRejected = isWeComIpRejected(code, message);
  const wrongEntry = /without context of external contact|no external contact context|当前没有外部联系人上下文|当前入口不支持/i.test(
    message
  );
  const trustedDomain = ipRejected || /invalid url domain|domain|url origin|可信域名/i.test(message);
  const permission =
    code === 48002 || code === 50001 || code === 84061 || /permission|no permission|not allow|权限/i.test(message);
  const signatureMismatch = /signature|invalid signature|签名/i.test(lowerMessage);

  return {
    step,
    message: ipRejected
      ? blockedIp
        ? `企业微信接口拒绝了当前服务器 IP。请将服务器出口 IP ${blockedIp} 配置到对应的企业可信 IP 或可信来源 IP。`
        : "企业微信接口拒绝当前服务器出口 IP。"
      : message,
    wecomErrCode: code,
    wecomErrMsg: publicError?.wecomErrMsg ?? null,
    browserError: publicError ? null : message,
    wrongEntry,
    trustedDomain,
    permission,
    signatureMismatch,
    blockedIp,
    actions: buildActions({
      publicError,
      wrongEntry,
      trustedDomain,
      permission,
      signatureMismatch,
      ipRejected,
      blockedIp
    }),
    getCurExternalContact: null
  };
}

function buildGetCurExternalContactDiagnosis(diagnostic: GetCurExternalContactDiagnostic): Diagnosis {
  const wrongEntry = diagnostic.classification === "no_customer_context";
  const permission = diagnostic.classification === "permission_denied";
  const signatureMismatch = diagnostic.classification === "signature_error";
  const message = getGetCurExternalContactMessage(diagnostic);

  return {
    ...createEmptyDiagnosis(),
    step: "getCurExternalContact",
    message,
    wecomErrCode: typeof diagnostic.errCode === "number" ? diagnostic.errCode : null,
    wecomErrMsg: diagnostic.errMsg,
    browserError: diagnostic.errMsg ? null : message,
    wrongEntry,
    trustedDomain: false,
    permission,
    signatureMismatch,
    blockedIp: null,
    actions: getGetCurExternalContactActions(diagnostic.classification),
    getCurExternalContact: diagnostic
  };
}

function getGetCurExternalContactMessage(diagnostic: GetCurExternalContactDiagnostic): string {
  switch (diagnostic.classification) {
    case "no_customer_context":
      return "企业微信没有向当前网页提供外部联系人上下文。请确认页面确实从外部联系人单聊工具栏打开。";
    case "permission_denied":
      return "当前自建应用或当前成员缺少客户联系相关权限。";
    case "api_not_supported":
      return "当前企业微信客户端或 SDK 环境不支持该接口，请检查客户端和 @wecom/jssdk 版本。";
    case "signature_error":
      return "getCurExternalContact 签名校验失败，请检查 URL 签名和 JS-SDK 注册状态。";
    case "unknown":
      return diagnostic.errMsg || "getCurExternalContact 调用失败，但当前返回信息不足以判断具体原因。";
  }
}

function getGetCurExternalContactActions(classification: GetCurExternalContactDiagnostic["classification"]): string[] {
  switch (classification) {
    case "no_customer_context":
      return ["确认页面确实从外部联系人单聊窗口的聊天工具栏打开。"];
    case "permission_denied":
      return ["检查当前自建应用和当前成员是否具备客户联系相关权限。"];
    case "api_not_supported":
      return ["检查企业微信客户端版本和 @wecom/jssdk 版本。"];
    case "signature_error":
      return ["重新初始化 JS-SDK，并确认当前页面 URL 与签名 URL 完全一致。"];
    case "unknown":
      return ["复制诊断结果，查看 getCurExternalContact.rawResult 中的原始返回。"];
  }
}

function createMockGetCurExternalContactDiagnostic(): GetCurExternalContactDiagnostic {
  const calledAt = new Date().toISOString();
  return {
    called: true,
    sdkMethod: "ww.getCurExternalContact",
    success: true,
    userId: "wm_mock_external_001",
    errMsg: null,
    errCode: null,
    rawResult: { userId: "wm_mock_external_001" },
    classification: "unknown",
    highLevelResult: { userId: "wm_mock_external_001" },
    rawInvokeResult: null,
    calledAt
  };
}

function buildActions(input: {
  publicError: PublicWeComError | null | undefined;
  wrongEntry: boolean;
  trustedDomain: boolean;
  permission: boolean;
  signatureMismatch: boolean;
  ipRejected: boolean;
  blockedIp: string | null;
}): string[] {
  const actions = new Set<string>();

  if (input.ipRejected) {
    actions.add(
      input.blockedIp
        ? `将服务器出口 IP ${input.blockedIp} 配置到企业微信后台的企业可信 IP 或可信来源 IP。`
        : "将服务器出口 IP 配置到企业微信后台的企业可信 IP 或可信来源 IP。"
    );
  }
  if (input.wrongEntry) {
    actions.add("确认页面是从外部联系人单聊的聊天工具栏打开。");
  }
  if (input.trustedDomain && !input.ipRejected) {
    actions.add("检查企业微信后台可信域名与 APP_BASE_URL 是否完全一致。");
  }
  if (input.permission) {
    actions.add("检查自建应用可见范围、客户联系权限和当前成员权限。");
  }
  if (input.signatureMismatch) {
    actions.add("确认签名 URL 移除了 hash，且 query 与浏览器实际地址一致。");
  }
  if (input.publicError?.operation) {
    actions.add(`后端失败操作：${input.publicError.operation}，保留 errcode 后去企业微信文档核对。`);
  }

  if (actions.size === 0) {
    actions.add("点击“重新检查诊断”，分别确认 access_token、config ticket、agent_config ticket。");
  }

  return Array.from(actions);
}

function sanitizeForCopy(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sanitizeForCopy);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => {
        const lower = key.toLowerCase();
        if (
          lower.includes("secret") ||
          lower.includes("token") ||
          lower.includes("ticket") ||
          lower.includes("cookie") ||
          lower === "state" ||
          lower === "code"
        ) {
          return [key, "[REDACTED]"];
        }
        return [key, sanitizeForCopy(item)];
      })
    );
  }

  return value;
}

function sanitizeUrlForDiagnostics(url: string): string {
  if (!url) {
    return "";
  }

  try {
    const parsed = new URL(url);
    for (const key of ["code", "state"]) {
      if (parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, "[REDACTED]");
      }
    }
    return parsed.toString();
  } catch {
    return url.replace(/([?&](?:code|state)=)[^&#]+/gi, "$1[REDACTED]");
  }
}

function formatFollowUserIds(followUserIds: string[] | undefined): string {
  if (!followUserIds || followUserIds.length === 0) {
    return "未返回";
  }

  return followUserIds.join(", ");
}
